// frontend/src/components/cashier/PayoutModal.js
import { api } from '../../services/api.js';
import { authService } from '../../services/authService.js';
import { Notification } from '../Notification.js';
import moment from 'moment-timezone';
import { printPayoutReceipt } from '../../utils/printService.js';
//import { showCustomConfirm } from '../../utils/domHelpers.js';
import { cashierContext } from '../../context/CashierContext.js';

const VENEZUELA_TIMEZONE = 'America/Caracas';

// ⭐ HELPER: Traductor de Matriz Exótica a Texto Hípico
function formatExoticBetForPrint(b) {
    if (b.type === 'Exotic' || (b.details && b.details.type)) {
        const det = b.details;
        if (!det || !det.selections) return det.type || 'Exótica';
        
        let formatted = det.type;
        if (det.isBox) {
            const h = new Set();
            for (let k in det.selections) {
                const col = k.split('_')[1];
                if (det.runners && det.runners[col]) h.add(det.runners[col].horse_number);
            }
            formatted = `${det.type} BOX (${Array.from(h).sort((a, b) => a - b).join(', ')})`;
        } else {
            const rows = {};
            let maxR = 0;
            for (let k in det.selections) {
                const [r, c] = k.split('_');
                if (!rows[r]) rows[r] = [];
                if (det.runners && det.runners[c]) rows[r].push(det.runners[c].horse_number);
                maxR = Math.max(maxR, parseInt(r));
            }
            const p = [];
            for (let i = 0; i <= maxR; i++) {
                p.push(rows[i] ? rows[i].sort((a, b) => a - b).join(', ') : 'X');
            }
            formatted = `${det.type} (${p.join(' / ')})`;
        }
        if (b.amount !== undefined) return `${formatted}: $${parseFloat(b.amount).toFixed(2)}`;
        return formatted;
    }
    return null;
}

// 1. Wrapper que crea el Modal Overlay y monta la vista adentro
export const openPayoutModal = (title) => {
    let overlay = document.getElementById('payout-modal-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'payout-modal-overlay';
        overlay.className = 'payout-modal-overlay';
        // Usamos los mismos estilos base que ya tienes en CashierPage.css
        overlay.innerHTML = `
            <div class="payout-modal-content" style="background-color: #1a652a; padding: 0; overflow: hidden; max-width: 800px; width: 95%;">
                <button class="payout-modal-close-btn" style="position: absolute; right: 15px; top: 15px; z-index: 100; color: #D4AF37; font-size: 2rem; background: none; border: none; cursor: pointer;">&times;</button>
                <div id="payout-content-area" style="width: 100%; height: 100%;"></div>
            </div>
        `;
        document.body.appendChild(overlay);
        
        overlay.querySelector('.payout-modal-close-btn').addEventListener('click', () => {
            overlay.classList.remove('visible');
            overlay.querySelector('#payout-content-area').innerHTML = ''; // Limpiar cámara y memoria
        });
    }

    const contentArea = overlay.querySelector('#payout-content-area');
    contentArea.innerHTML = ''; 
    
    // Inyectamos la página exacta de tu código original
    const payoutElement = PayoutPage({ title });
    contentArea.appendChild(payoutElement);
    
    overlay.classList.add('visible');
};

// 2. TU CÓDIGO ORIGINAL INTACTO DE PayoutPage
function PayoutPage({ title }) {
    const page = document.createElement('div');
    page.className = 'page payout-page w-full h-full min-h-[400px] flex flex-col items-center justify-center';

    const isVoidAction = title === 'Anular Ticket';

    // Definimos tema
    const theme = isVoidAction ? {
        bgMain: '#7f1d1d',
        bgCard: '#991b1b',
        bgInput: '#450a0a',
        border: '#D4AF37',
        textMain: '#ffffff',
        textGold: '#D4AF37',
        btnPrimary: '#ef4444',
        btnHover: '#dc2626'
    } : {
        bgMain: '#14532d',
        bgCard: '#064e3b',
        bgInput: '#022c22',
        border: '#D4AF37',
        textMain: '#ffffff',
        textGold: '#D4AF37',
        btnPrimary: '#064e3b',
        btnHover: '#047857'
    };

    const styleElement = document.createElement('style');
    styleElement.textContent = `
        .hidden { display: none !important; }
        
        .payout-page {
            background-color: ${theme.bgMain} !important;
            color: ${theme.textMain} !important;
            border-radius: 0.5rem;
            padding: 2rem;
            box-shadow: inset 0 0 50px rgba(0,0,0,0.5);
        }

        #ticketCodeInput {
            background-color: ${theme.bgInput} !important;
            color: ${theme.textGold} !important;
            border: 2px solid ${theme.border} !important;
            padding: 12px;
            font-size: 1.3em;
            text-align: center;
            font-weight: bold;
            box-shadow: inset 0 2px 5px rgba(0,0,0,0.5);
        }
        #ticketCodeInput::placeholder { color: rgba(255,255,255, 0.4); }

        /* Botones Generales */
        #scanTicketBtn {
            background-color: ${theme.btnPrimary} !important;
            color: white !important;
            border: 1px solid ${theme.border} !important;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        #scanTicketBtn:hover {
            background-color: ${theme.btnHover} !important;
            box-shadow: 0 0 15px ${theme.border};
            transform: translateY(-2px);
        }

        /* Botón Cámara (Móvil) */
        #cameraScanBtn {
            background-color: #2563eb !important; /* Azul */
            color: white !important;
            border: 1px solid ${theme.border} !important;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        /* Botón Ver Último */
        #viewLastTicketBtn {
            background-color: ${theme.bgInput} !important;
            color: #ccc !important;
            border: 1px solid #666 !important;
        }

        /* 📱 MOBILE: Ocultar botón "Ver Último" en pantallas pequeñas */
        @media (max-width: 768px) {
            #viewLastTicketBtn {
                display: none !important; /* Invisible en móviles */
            }
            .payout-page {
                padding: 1rem;
            }
        }

        /* Detalles del Ticket */
        .payout-details {
            background-color: ${theme.bgCard} !important;
            border: 2px solid ${theme.border} !important;
            color: ${theme.textMain} !important;
            box-shadow: 0 10px 25px rgba(0,0,0,0.5) !important;
        }
        .payout-details h4 {
            color: ${theme.textGold} !important;
            border-bottom: 1px solid ${theme.border};
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
        }
        .payout-details p {
            color: ${theme.textMain} !important;
            font-size: 1.1rem;
            margin-bottom: 0.5rem;
        }
        .payout-details strong { color: ${theme.textGold} !important; }

        /* Modal de Confirmación Interno */
        .internal-confirmation-overlay {
            position: fixed !important;
            top: 0; left: 0;
            width: 100vw; height: 100vh;
            background-color: rgba(0, 0, 0, 0.9) !important;
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000 !important;
        }
        .internal-modal-content {
            background-color: ${theme.bgMain} !important;
            border: 2px solid ${theme.border} !important;
            color: ${theme.textMain} !important;
            padding: 2rem;
            border-radius: 1rem;
            text-align: center;
            box-shadow: 0 0 30px rgba(212, 175, 55, 0.3);
            max-width: 90%;
            width: 400px;
        }
        .confirm-button {
            background-color: #15803d !important; color: white; padding: 10px 25px; border-radius: 5px; cursor: pointer; border: 1px solid ${theme.border}; font-weight:bold; margin: 5px;
        }
        .cancel-button {
            background-color: #b91c1c !important; color: white; padding: 10px 25px; border-radius: 5px; cursor: pointer; border: 1px solid white; font-weight:bold; margin: 5px;
        }

        /* 📸 ESTILOS DE ESCANER FLUIDO (CORREGIDOS) */
        #scanner-overlay {
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: #000; z-index: 20000;
            overflow: hidden; /* Evitar scroll */
        }
        
        /* El contenedor del lector debe ocupar todo */
        #reader { 
            width: 100% !important; 
            height: 100% !important; 
            border: none !important;
            position: absolute;
            top: 0; left: 0;
        }
        
        /* 🔥 CLAVE: Forzar al video a llenar la pantalla sin bordes negros */
        #reader video {
            object-fit: cover !important;
            width: 100% !important;
            height: 100% !important;
            border-radius: 0 !important;
        }
        
        /* Capa de interfaz flotante */
        .scanner-ui-layer {
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            display: flex; 
            flex-direction: column; 
            justify-content: center; /* Centrado vertical perfecto */
            align-items: center;     /* Centrado horizontal perfecto */
            pointer-events: none;    /* Dejar pasar clicks al video si fuera necesario */
            z-index: 20001;
        }

        .scan-frame {
            width: 280px; height: 280px;
            border: 4px solid ${theme.textGold};
            border-radius: 25px;
            /* Sombra gigante para oscurecer el fondo fuera del cuadro */
            box-shadow: 0 0 0 4000px rgba(0,0,0,0.6); 
            position: relative;
            /* Efecto de brillo en el borde */
            box-shadow: 0 0 0 4000px rgba(0,0,0,0.6), inset 0 0 20px rgba(212, 175, 55, 0.3);
        }
        
        /* Esquinas animadas para dar sensación de escaneo */
        .scan-frame::after {
            content: ''; position: absolute; top: -10px; left: -10px; right: -10px; bottom: -10px;
            border: 4px solid transparent;
            border-top-color: #fff; border-bottom-color: #fff;
            border-radius: 30px;
            animation: pulse-border 2s infinite;
            opacity: 0.5;
        }

        @keyframes pulse-border {
            0% { transform: scale(1); opacity: 0.8; }
            100% { transform: scale(1.05); opacity: 0; }
        }

        .scan-instruction {
            position: absolute;
            bottom: 140px; /* Posición fija relativa al fondo */
            color: #fff; font-size: 1.1rem; font-weight: bold;
            text-shadow: 0 2px 4px rgba(0,0,0,0.8);
            background: rgba(0,0,0,0.5); padding: 8px 20px; border-radius: 20px;
            pointer-events: auto;
        }

        .scanner-controls {
            position: absolute;
            bottom: 40px;
            left: 0; 
            width: 100%;
            display: flex; 
            gap: 30px; 
            justify-content: center; 
            align-items: center;
            pointer-events: auto;
        }

        #close-scanner-btn { 
            padding: 15px 40px; 
            background: #b91c1c; 
            color: white; 
            border: none; 
            font-size: 1.1rem; 
            border-radius: 50px; 
            font-weight: bold; 
            box-shadow: 0 4px 15px rgba(0,0,0,0.5);
            cursor: pointer;
            letter-spacing: 1px;
        }
    `;
    page.appendChild(styleElement);

    const mainContent = document.createElement('main');
    mainContent.className = 'w-full max-w-2xl flex flex-col items-center';
    page.appendChild(mainContent);

    const h2 = document.createElement('h2');
    h2.textContent = title || 'Gestión de Boletos';
    h2.className = 'text-3xl font-bold mb-8 uppercase tracking-widest text-center';
    h2.style.color = theme.textGold;
    h2.style.textShadow = '2px 2px 4px rgba(0,0,0,0.8)';
    mainContent.appendChild(h2);

    const notificationContainer = document.createElement('div');
    notificationContainer.className = 'w-full mb-6';
    mainContent.appendChild(notificationContainer);

    // --- DETECCIÓN DE MÓVIL ---
    const isMobile = window.innerWidth <= 768;

    // Botón de cámara (solo visible en móviles)
    const cameraButtonHtml = isMobile 
        ? `<button class="btn py-3 px-6 rounded-lg transition duration-300 shadow-lg" id="cameraScanBtn" style="display: flex; align-items: center; gap: 8px;"><span>📷</span> ESCANEAR</button>` 
        : '';

    const scanArea = document.createElement('div');
    scanArea.className = 'payout-scan-area flex flex-col items-center gap-6 w-full';
    scanArea.innerHTML = `
        <p style="color: ${theme.textMain}; font-size: 1.1rem;">Ingresa el ID o escanea el código del boleto:</p>
        <input type="text" id="ticketCodeInput" placeholder="Código del Boleto" class="w-full max-w-md rounded-lg shadow-inner">
        <div class="flex flex-wrap justify-center gap-4 w-full mt-2">
            <button class="btn py-3 px-8 rounded-lg transition duration-300 shadow-lg text-lg" id="scanTicketBtn">🔍 VERIFICAR</button>
            ${cameraButtonHtml}
            <button class="btn py-3 px-6 rounded-lg transition duration-300 shadow-lg" id="viewLastTicketBtn">↺ Ver Último</button>
        </div>
    `;
    mainContent.appendChild(scanArea);

    const ticketDetailsArea = document.createElement('div');
    ticketDetailsArea.id = 'ticketDetailsArea';
    ticketDetailsArea.className = 'payout-details hidden p-8 rounded-xl mt-10 w-full text-left';
    mainContent.appendChild(ticketDetailsArea);

    const ticketCodeInput = scanArea.querySelector('#ticketCodeInput');
    const scanTicketBtn = scanArea.querySelector('#scanTicketBtn');
    const viewLastTicketBtn = scanArea.querySelector('#viewLastTicketBtn');
    
    // Referencia al botón de cámara
    const cameraScanBtn = scanArea.querySelector('#cameraScanBtn');

    let currentTicketId = null;

    // --- FUNCIONES LÓGICAS ---

    const displayTicketDetails = (ticket, message = 'Detalles obtenidos', statusOverride = '', type = 'info') => {
        currentTicketId = ticket.id;
        const ticketAmount = parseFloat(ticket.amount);
        const currency = ticket.currency || 'USD';
        const currencySymbol = currency === 'VES' ? 'Bs.' : '$';

        const refundedAmount = parseFloat(ticket.refunded_amount || 0);
        const refundedHorses = ticket.refunded_horses || [];
        let isTotalRefund = false;
        let isPartialRefund = false;
        const premioAmount = parseFloat(ticket.potential_winnings || ticket.paid_amount || 0);

        if (refundedAmount > 0) {
            if (Math.abs(premioAmount - refundedAmount) < 0.01) isTotalRefund = true;
            else isPartialRefund = true;
        }

        let estadoTicketHumano = '';
        if (ticket.status === 'paid') estadoTicketHumano = '<span class="text-green-400 font-bold">✅ PAGADO</span>';
        else if (ticket.status === 'voided') estadoTicketHumano = '<span class="text-red-500 font-bold">🚫 ANULADO</span>';
        else estadoTicketHumano = '<span class="text-yellow-400 font-bold">⏳ PENDIENTE</span>';

        let estadoPremioHumano = '';
        if (ticket.is_winner === true || ticket.prize_status === 'winner') {
            if (isTotalRefund) estadoPremioHumano = `<span class="text-yellow-400 font-bold">🔄 REEMBOLSO TOTAL (Devuelve: ${currencySymbol}${premioAmount.toFixed(2)})</span>`;
            else if (isPartialRefund) estadoPremioHumano = `<span class="text-green-400 font-bold">🏆 ¡GANADOR + 🔄 REEMB! (Total: ${currencySymbol}${premioAmount.toFixed(2)})</span><div class="text-sm text-yellow-300 mt-1">Premio Neto: ${currencySymbol}${(premioAmount - refundedAmount).toFixed(2)} | Devolución: ${currencySymbol}${refundedAmount.toFixed(2)}</div>`;
            else estadoPremioHumano = `<span class="text-green-400 font-bold">🏆 ¡GANADOR! (Paga: ${currencySymbol}${premioAmount.toFixed(2)})</span>`;
        } else if (ticket.is_winner === false || ticket.prize_status === 'loser') {
            estadoPremioHumano = '<span class="text-gray-400 font-bold">❌ PERDEDOR</span>';
        } else {
            estadoPremioHumano = '<span class="text-gray-300 font-bold">⏳ Esperando Resultados</span>';
        }

        let statusStyle = `color: ${theme.textGold}; font-weight: bold; font-size: 1.2rem; text-align: center; margin-top: 1.5rem; border-top: 1px dashed ${theme.border}; padding-top: 1rem;`;
        if (type === 'error') statusStyle = 'color: #ef4444; font-weight: bold; font-size: 1.2rem; text-align: center; margin-top: 1.5rem; background: rgba(0,0,0,0.4); padding: 15px; border-radius: 8px; border: 1px solid #ef4444;';
        else if (type === 'success') statusStyle = 'color: #4ade80; font-weight: bold; font-size: 1.2rem; text-align: center; margin-top: 1.5rem; background: rgba(0,0,0,0.4); padding: 15px; border-radius: 8px; border: 1px solid #4ade80;';
        else if (type === 'warning') statusStyle = 'color: #facc15; font-weight: bold; font-size: 1.2rem; text-align: center; margin-top: 1.5rem; background: rgba(0,0,0,0.4); padding: 15px; border-radius: 8px; border: 1px solid #facc15;';

        const carreraTexto = ticket.track_name ? `${ticket.track_name} - C${ticket.race_number || '?'}` : `Carrera #${ticket.race_number || '?'}`;
        const fechaTicket = ticket.created_at ? moment.utc(ticket.created_at).tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY hh:mm A') : 'N/A';

        // ⭐ TRADUCCIÓN VISUAL DE CABALLOS (INCLUYE EXÓTICAS)
        let caballosTexto = 'N/A';
        if (ticket.mixed_bets_details && Array.isArray(ticket.mixed_bets_details) && ticket.mixed_bets_details.length > 0) {
            caballosTexto = ticket.mixed_bets_details.map(b => {
                // Si es exótica usamos el helper y no tachamos porque las exóticas hacen reembolso parcial matemático
                const exoText = formatExoticBetForPrint(b);
                if (exoText) return `<span style="font-size:1.1rem; color:#D4AF37;">${exoText.split(':')[0]} <br><span style="color:#a7f3d0; font-size:0.9rem;">(Base: $${parseFloat(b.amount/b.details.combinations).toFixed(2)})</span></span>`;

                const hNum = ticket.horse_numbers && ticket.horse_numbers[ticket.mixed_bets_details.indexOf(b)] ? ticket.horse_numbers[ticket.mixed_bets_details.indexOf(b)] : b.horse_id;
                const isRef = refundedHorses.includes(parseInt(hNum));
                const lineThrough = isRef ? 'text-decoration: line-through; color: #ef4444;' : '';
                const refBadge = isRef ? ' <span style="font-size:0.8rem; color:#facc15;">[RETIRADO]</span>' : '';
                return `<span style="font-size:1.1rem; ${lineThrough}">#${hNum} ${b.type.toUpperCase()}: ${currencySymbol}${parseFloat(b.amount).toFixed(2)}</span>${refBadge}`;
            }).join('<br>');
        } else if (ticket.horse_numbers && Array.isArray(ticket.horse_numbers) && ticket.horse_numbers.length > 0) {
            caballosTexto = ticket.horse_numbers.map(n => {
                const isRef = refundedHorses.includes(parseInt(n));
                return isRef ? `<span style="text-decoration: line-through; color: #ef4444;">#${n}</span>` : `#${n}`;
            }).join(' - ');
        } else if (ticket.selected_horses_ids) {
            caballosTexto = ticket.selected_horses_ids.join(' - ');
        }

        const defaultBottomText = (ticket.prize_status === 'winner' && ticket.status === 'pending') 
            ? '💰 Boleto verificado. ¡Listo para pagar premio!' 
            : 'Consulte el estado de la resolución arriba.';

        ticketDetailsArea.innerHTML = `
            <h4 class="text-2xl font-bold mb-2 uppercase text-center" style="border-bottom:none;">🎫 DETALLES DEL BOLETO</h4>
            <p class="text-center text-gray-300 text-sm mb-6 pb-4" style="border-bottom: 1px solid rgba(212,175,55,0.3);">Emitido: ${fechaTicket}</p>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 mb-4 text-lg">
                <p><strong>Referencia:</strong> <span class="font-mono text-yellow-300">${ticket.public_code || ticket.id}</span></p>
                <p><strong>Monto Jugado:</strong> <span class="text-white">${currencySymbol}${ticketAmount.toFixed(2)}</span></p>
                <p><strong>Tipo Apuesta:</strong> <span class="text-white">${ticket.bet_type_name || 'Directa'}</span></p>
                <p><strong>Carrera:</strong> <span style="color: ${theme.textGold}">${carreraTexto}</span></p>
                <p><strong>Estado en Caja:</strong> ${estadoTicketHumano}</p>
                <p><strong>Resolución:</strong> ${estadoPremioHumano}</p>
                
                <div class="col-span-1 md:col-span-2 mt-4 p-4 rounded-lg" style="background: rgba(0,0,0,0.3); border: 1px solid rgba(212,175,55,0.2);">
                    <p class="text-center mb-2" style="color: ${theme.textGold}; font-size:1rem;"><strong>EJEMPLARES / APUESTAS</strong></p>
                    <p class="text-center" style="color:#fff; font-size:1.4rem; font-weight:bold; line-height: 1.4;">
                        ${caballosTexto}
                    </p>
                </div>
            </div>
            
            <div style="${statusStyle}">
                ${statusOverride ? statusOverride : defaultBottomText}
            </div>
        `;
        
        ticketDetailsArea.classList.remove('hidden');
        Notification(notificationContainer, message, type);
    };

    // ⭐ Ventana de confirmación elegante para Pagos y Anulaciones
    const showCustomConfirm = (message, onConfirmCallback) => {
        if (document.getElementById('internal-confirm-modal')) return;

        const modalOverlay = document.createElement('div');
        modalOverlay.id = 'internal-confirm-modal';
        modalOverlay.className = 'internal-confirmation-overlay';
        modalOverlay.style.zIndex = '10500';
        modalOverlay.style.backgroundColor = 'rgba(0, 15, 5, 0.85)';
        modalOverlay.style.backdropFilter = 'blur(4px)';

        modalOverlay.innerHTML = `
            <style>
                .custom-confirm-modal {
                    background: linear-gradient(180deg, #1a652a 0%, #0f381a 100%);
                    border: 2px solid #D4AF37;
                    box-shadow: 0 0 50px rgba(0,0,0,0.8), 0 0 20px rgba(212, 175, 55, 0.2);
                    border-radius: 1rem;
                    padding: 2.5rem;
                    width: 90%;
                    max-width: 420px;
                    display: flex;
                    flex-direction: column;
                    animation: popIn 0.3s ease-out forwards;
                }
                @keyframes popIn {
                    from { opacity: 0; transform: scale(0.9); }
                    to { opacity: 1; transform: scale(1); }
                }
                .confirm-btn-cancel, .confirm-btn-action {
                    flex: 1;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    font-weight: bold;
                    font-size: 1rem;
                    text-transform: uppercase;
                    cursor: pointer;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.2);
                    transition: all 0.2s;
                }
                .confirm-btn-cancel {
                    background-color: #7f1d1d;
                    color: white;
                    border: 1px solid #D4AF37;
                }
                .confirm-btn-cancel:hover { background-color: #991b1b; transform: translateY(-2px); }
                
                .confirm-btn-action {
                    background-color: #064e3b;
                    color: #D4AF37;
                    border: 1px solid #D4AF37;
                }
                .confirm-btn-action:hover { background-color: #047857; color: white; transform: translateY(-2px); box-shadow: 0 0 10px rgba(212, 175, 55, 0.4); }
            </style>
            
            <div class="custom-confirm-modal" role="alertdialog">
                <h2 style="text-align: center; color: #D4AF37; font-size: 1.6rem; margin-top: 0; margin-bottom: 1.5rem; border-bottom: 1px solid rgba(212, 175, 55, 0.3); padding-bottom: 15px; font-weight: 800; text-transform: uppercase;">
                    ⚠️ Confirmación
                </h2>
                
                <p style="color: #e5e7eb; font-size: 1.2rem; text-align: center; margin-bottom: 2rem; line-height: 1.4;">
                    ${message}
                </p>
                
                <div style="display: flex; gap: 1rem; border-top: 1px solid rgba(212, 175, 55, 0.3); padding-top: 1.5rem;">
                    <button id="cancel-action-btn" class="confirm-btn-cancel">Cancelar</button>
                    <button id="confirm-action-btn" class="confirm-btn-action">✅ Procesar</button>
                </div>
            </div>
        `;
        document.body.appendChild(modalOverlay);

        const closeModal = () => { if (document.body.contains(modalOverlay)) document.body.removeChild(modalOverlay); };

        modalOverlay.querySelector('#confirm-action-btn').addEventListener('click', () => { onConfirmCallback(); closeModal(); });
        modalOverlay.querySelector('#cancel-action-btn').addEventListener('click', closeModal);
        modalOverlay.querySelector('#confirm-action-btn').focus();
    };
    
    const handlePayTicket = (ticket) => {
        const potentialWinnings = parseFloat(ticket.potential_winnings);
        const currencySymbol = ticket.currency === 'VES' ? 'Bs.' : '$';
        console.log(`[PAYOUT-MODAL DEBUG] Solicitando pago de Ticket #${ticket.id}. Monto a mostrar y pagar: ${potentialWinnings}`);
        showCustomConfirm(`¿Pagar premio de <strong style="color:#4ade80; font-size:1.4em;">${currencySymbol}${potentialWinnings.toFixed(2)}</strong>?`, async () => {
            try {
                console.log(`[PAYOUT-MODAL DEBUG] Enviando POST a /tickets/pay/${ticket.id}`);
                const payResponse = await api.post(`/tickets/pay/${ticket.id}`);
                console.log(`[PAYOUT-MODAL DEBUG] Respuesta del pago:`, payResponse);
                Notification(notificationContainer, payResponse.message, 'success');
                ticketDetailsArea.classList.add('hidden');
                ticketCodeInput.value = '';
                
                // ⭐ IMPRIMIR EL RECIBO DE PAGO AL CONFIRMAR
                if (payResponse.ticket) {
                    printPayoutReceipt(payResponse.ticket);
                }
            } catch (payError) {
                Notification(notificationContainer, payError.message || 'Error al procesar el pago.', 'error');
            }
        });
    };

    /**
     * ⭐ NUEVA FUNCIÓN: Genera e imprime el comprobante de pago de premio.
     * @param {object} ticketDetails - Detalles de la respuesta de pago.
     */
    /**
     * Genera e imprime el comprobante de pago de premio.
     */
    async function printPayoutReceipt(ticketDetails) {
        if (!ticketDetails || (!ticketDetails.ticket_code && !ticketDetails.id)) return;

        const printWindow = window.open('', '_blank', 'width=350,height=500');
        if (!printWindow) {
            Notification(notificationContainer, 'Permita las ventanas emergentes.', 'warning');
            return;
        }

        const currencySymbol = ticketDetails.currency === 'VES' ? 'Bs.' : '$';
        const formattedDate = moment.utc(ticketDetails.paid_at || new Date()).tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY');
        const formattedTime = moment.utc(ticketDetails.paid_at || new Date()).tz(VENEZUELA_TIMEZONE).format('hh:mm A');
        const horsesText = Array.isArray(ticketDetails.horse_numbers) ? ticketDetails.horse_numbers.map(n => `#${n}`).join(', ') : 'N/A';

        const receiptHtmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Pago</title>
                <style>
                    /* ESTÁNDAR 58mm PARA IMPRESORAS TÉRMICAS TIPO POS-5890K */
                    @page { margin: 0; size: 58mm auto; }
                    html, body { 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        background: #fff;
                        width: 100%;
                    }
                    .thermal-ticket {
                        font-family: 'Courier New', Courier, monospace;
                        font-size: 11px;
                        font-weight: 900 !important; 
                        color: #000;
                        width: 58mm; 
                        max-width: 100%;
                        margin: 0 auto;
                        padding: 2mm 2mm 15mm 2mm; 
                        box-sizing: border-box;
                        line-height: 1.2;
                        text-align: left;
                        overflow: hidden;
                    }
                    .thermal-ticket * { font-weight: 900 !important; }
                    .center { text-align: center; }
                    .divider { border-top: 1px dashed #000; margin: 4px 0; width: 100%; }
                    .break-word { word-wrap: break-word; word-break: break-all; }
                </style>
            </head>
            <body>
                <div class="thermal-ticket">
                    *** PAGO PREMIO ***<br>
                    ${formattedDate} - ${formattedTime}<br>
                    ========================<br>
                    TICKET PAGADO:${ticketDetails.id}<br>
                    ========================<br>
                    HIPODROMO: ${ticketDetails.track_name || 'N/A'}<br>
                    CARRERA: C-${ticketDetails.race_number || 'N/A'}<br>
                    ------------------------<br>
                    EJEMPLAR N°${horsesText}<br>
                    ------------------------<br>
                    APOSTADO:${currencySymbol}${parseFloat(ticketDetails.wager_amount || ticketDetails.amount || 0).toFixed(2)}<br>
                    ========================<br>
                    PREMIO PAGADO:${currencySymbol}${parseFloat(ticketDetails.paid_amount).toFixed(2)} ${ticketDetails.currency || 'USD'}<br>
                    ========================<br>
                    <span style="font-size: 5px;">
                        Felicidades por su premio
                    </span><br>
                    <br><br><br><br>
                    . . . . . . . . . . . .
                </div>
                <script>
                    window.onload = () => {
                        window.onafterprint = () => window.close();
                        setTimeout(() => window.print(), 300);
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(receiptHtmlContent);
        printWindow.document.close();
    }
    /**
     * ⭐ NUEVA FUNCIÓN: Genera e imprime el Ticket Z de Cuadre de Caja
     */
    function printReconciliationReceipt(reconData) {
        const printWindow = window.open('', '_blank', 'width=320,height=600,resizable=yes,scrollbars=yes');
        if (!printWindow) {
            Notification(notificationContainer, 'Por favor, permita las ventanas emergentes para imprimir.', 'warning');
            return;
        }

        const printTime = moment().tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY HH:mm');

        const receiptHtmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Reporte de Caja (Z)</title>
                <style>
                    /* ESTÁNDAR 58mm PARA IMPRESORAS TÉRMICAS TIPO POS-5890K */
                    @page { margin: 0; size: 58mm auto; }
                    html, body { 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        background: #fff;
                        width: 100%;
                    }
                    .thermal-ticket {
                        font-family: 'Courier New', Courier, monospace;
                        font-size: 11px;
                        font-weight: 900 !important; 
                        color: #000;
                        width: 58mm; 
                        max-width: 100%;
                        margin: 0 auto;
                        padding: 2mm 2mm 15mm 2mm; 
                        box-sizing: border-box;
                        line-height: 1.2;
                        text-align: left;
                        overflow: hidden;
                    }
                    .thermal-ticket * { font-weight: 900 !important; }
                    .center { text-align: center; }
                    .divider { border-top: 1px dashed #000; margin: 4px 0; width: 100%; }
                    .row { display: flex; justify-content: space-between; width: 100%; }
                    .section-title { font-size: 12px; text-decoration: underline; margin-top: 6px; margin-bottom: 3px; }
                </style>
            </head>
            <body>
                <h2>TAQUILLA HÍPICA</h2>
                <h3>REPORTE DE CAJA (Z)</h3>
                <div class="divider"></div>
                <p>Fecha Imp: ${printTime}</p>
                <p>Cajero: ${reconData.cashierName}</p>
                <p>Periodo: ${reconData.dateRange}</p>
                <p>Tasa Cambio: ${reconData.rate.toFixed(2)} Bs/$</p>
                
                <div class="divider"></div>
                
                <div class="section-title">RESUMEN DÓLARES (USD)</div>
                <div class="row"><span>Caja Inicial:</span> <span>$${reconData.initialCash.toFixed(2)}</span></div>
                <div class="row"><span>(+) Ventas:</span> <span>$${reconData.usd.sales.toFixed(2)}</span></div>
                <div class="row"><span>(-) Pagos:</span> <span>-$${reconData.usd.paid.toFixed(2)}</span></div>
                <div class="row"><span>(i) Anulados:</span> <span>$${reconData.usd.voided.toFixed(2)}</span></div>
                <div class="row bold"><span>TOTAL EN CAJA USD:</span> <span>$${reconData.usd.net.toFixed(2)}</span></div>

                <div class="divider"></div>

                <div class="section-title">RESUMEN BOLÍVARES (VES)</div>
                <div class="row"><span>(+) Ventas:</span> <span>Bs.${reconData.ves.sales.toFixed(2)}</span></div>
                <div class="row"><span>(-) Pagos:</span> <span>-Bs.${reconData.ves.paid.toFixed(2)}</span></div>
                <div class="row"><span>(i) Anulados:</span> <span>Bs.${reconData.ves.voided.toFixed(2)}</span></div>
                <div class="row bold"><span>TOTAL EN CAJA VES:</span> <span>Bs.${reconData.ves.net.toFixed(2)}</span></div>

                <div class="divider"></div>
                
                <h4 style="margin-top: 10px;">TOTAL CONSOLIDADO (APROX)</h4>
                <div class="row bold" style="font-size: 14px; margin-top:5px;"><span>NETO USD:</span> <span>$${reconData.totalNetUSD.toFixed(2)}</span></div>
                <div class="row bold" style="font-size: 14px;"><span>NETO VES:</span> <span>Bs.${reconData.totalNetVES.toFixed(2)}</span></div>

                <div class="divider"></div>
                <p>Tickets Procesados: ${reconData.totalTickets}</p>
                <p style="margin-top:15px; font-size:10px;">FIN DEL REPORTE</p>
                
                <script>
                    document.addEventListener('DOMContentLoaded', () => {
                        setTimeout(() => {
                            window.onafterprint = () => window.close();
                            window.print();
                        }, 500);
                    });
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(receiptHtmlContent);
        printWindow.document.close();
    }

    const handleVoidTicket = (ticket) => {
        showCustomConfirm(`¿ANULAR boleto <strong style="color:#ef4444; font-size:1.3em;">${ticket.ticket_code}</strong>? <br><br><span style="font-size:0.9em; color:#ccc;">Esta acción no se puede deshacer.</span>`, async () => {
            try {
                const voidResponse = await api.put(`/tickets/${ticket.id}/void`);
                Notification(notificationContainer, voidResponse.message, 'success');
                ticketDetailsArea.classList.add('hidden');
                ticketCodeInput.value = '';
            } catch (voidError) {
                Notification(notificationContainer, voidError.message || 'Error al anular el boleto.', 'error');
            }
        });
    };

    const verifyAndProcessTicket = async () => {
        const identifier = ticketCodeInput.value.trim();
        console.log(`[PayoutModal DEBUG] 1. Iniciando verificación con identificador: "${identifier}"`);

        if (!identifier) { Notification(notificationContainer, 'Ingrese un código.', 'warning'); return; }

        ticketDetailsArea.classList.add('hidden');
        // ⭐ MEJORA: Se unifica la búsqueda en un solo endpoint para evitar ambigüedad
        // entre IDs numéricos y códigos públicos que puedan ser numéricos.
        // El backend en /scan/ ahora puede buscar por ID, public_code o ticket_code.
        const apiUrl = `/tickets/scan/${identifier}`;
        console.log(`[PayoutModal DEBUG] 2. URL de API construida:`, apiUrl);

        try {
            const response = await api.get(apiUrl);
            console.log('[PayoutModal DEBUG] 3. Respuesta CRUDA de la API:', JSON.stringify(response, null, 2));

            const ticketData = response.ticket || response; 
            console.log('[PayoutModal DEBUG] 4. Datos del ticket extraídos:', ticketData);

            if (!ticketData || !ticketData.id) throw new Error("Boleto no encontrado.");

            // ⭐ DETECTAR SI ES CÓDIGO PÚBLICO
            // Comparamos el input con el public_code que viene del backend
            const isPublicScan = (ticketData.public_code === identifier) || (ticketData.public_code === identifier.toUpperCase());

            displayTicketDetails(ticketData, 'Verificando estatus...');

            if (title === 'Pagar Premio') {
                
                // ⭐ BLOQUEO: Si es código público, NO permitir pagar
                if (isPublicScan) {
                    displayTicketDetails(ticketData, 'Boleto consultado por Referencia.', 'VISTA PÚBLICA (NO PAGAR)', 'warning');
                    return; // Detiene la ejecución aquí, no llama a handlePayTicket
                }

                // Lógica normal de pago (solo con código ticket_code o ID)
                if (ticketData.status === 'paid') {
                    displayTicketDetails(ticketData, 'ERROR: Boleto ya pagado.', 'YA PAGADO', 'error');
                } else if (ticketData.status === 'voided') {
                    displayTicketDetails(ticketData, 'ERROR: Boleto anulado.', 'ANULADO', 'error');
                } else if (ticketData.prize_status === 'winner' && ticketData.status === 'pending') {
                    handlePayTicket(ticketData);
                } else if (ticketData.prize_status === 'loser') {
                    displayTicketDetails(ticketData, 'Boleto Perdedor.', 'NO GANADOR', 'warning');
                } else {
                    displayTicketDetails(ticketData, 'No se puede pagar.', `Estado: ${ticketData.prize_status}`, 'warning');
                }

            } else if (title === 'Anular Ticket') {
                // ⭐ LÓGICA DE ANULACIÓN CORREGIDA: Permitida en MTP 0 ('final', 'race_off').
                // Solo se prohíbe la anulación cuando la carrera está explícitamente CERRADA o FINALIZADA en base de datos.
                const raceStatus = (ticketData.race_status || '').toLowerCase();
                
                // MTP 0 suele arrojar 'final' o 'race_off'. Ahora estos estados SÍ permiten anulación.
                const restrictedStatuses = ['closed', 'finished'];

                // 1. Verificamos el estatus de la carrera desde la API local
                if (restrictedStatuses.includes(raceStatus)) {
                     displayTicketDetails(ticketData, 'No se puede anular: La carrera ya cruzó la meta.', `Carrera: ${raceStatus.toUpperCase()}`, 'error');
                     return;
                }
                
                // 2. Verificamos que el ticket siga pendiente de pago/anulación
                if (ticketData.status === 'pending') {
                    handleVoidTicket(ticketData);
                } else {
                    displayTicketDetails(ticketData, 'No se puede anular.', `Estado del Boleto: ${ticketData.status}`, 'error');
                }
            } else {
                displayTicketDetails(ticketData);
            }

        } catch (error) {
            ticketDetailsArea.classList.add('hidden');
            console.error('[PayoutModal DEBUG] 5. ¡ERROR CAPTURADO!', error);
            Notification(notificationContainer, error.message || 'Boleto no encontrado.', 'error');
        }
    };

    scanTicketBtn.addEventListener('click', verifyAndProcessTicket);
    ticketCodeInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') scanTicketBtn.click(); });

    // Si existe el botón (versión escritorio), agregar evento
    if (viewLastTicketBtn) {
        viewLastTicketBtn.addEventListener('click', async () => {
            const currentUser = authService.getCurrentUser();
            console.log(`[PayoutModal DEBUG] 1. Buscando último ticket para cajero ID: ${currentUser?.id}`);
            if (!currentUser?.id) return Notification(notificationContainer, 'Error de sesión.', 'error');
            try {
                // Se realiza la petición para obtener el último boleto
                const response = await api.get(`/tickets/last-by-cashier/${currentUser.id}`);
                console.log('[PayoutModal DEBUG] 2. Respuesta CRUDA de la API (último ticket):', JSON.stringify(response, null, 2));

                // ⭐ CORRECCIÓN: Se utiliza una lógica más robusta para extraer el ticket.
                // Esta lógica asegura que encontremos el ticket incluso si la estructura de la respuesta varía.
                const ticketData = response.ticket || response;
                console.log('[PayoutModal DEBUG] 3. Datos del último ticket extraídos:', ticketData);

                if (ticketData && ticketData.id) {
                    displayTicketDetails(ticketData, 'Último boleto encontrado.');
                } else {
                    Notification(notificationContainer, response.message || 'No hay boletos recientes.', 'info');
                }
            } catch (error) { console.error('[PayoutModal DEBUG] 4. ¡ERROR CAPTURADO (último ticket)!', error); Notification(notificationContainer, 'Error buscando último boleto.', 'error'); }
        });
    }

    // --- 📸 LÓGICA DE ESCANEO "REAR-ONLY" ---
    if (cameraScanBtn) {
        let html5QrCode = null;

        const loadScannerLibrary = () => {
            return new Promise((resolve, reject) => {
                if (window.Html5Qrcode) return resolve();
                const script = document.createElement('script');
                script.src = "https://unpkg.com/html5-qrcode";
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        };

        const startScanning = async () => {
            try {
                await loadScannerLibrary();
                
                // UI Personalizada sin botón de cambio
                const scannerOverlay = document.createElement('div');
                scannerOverlay.id = 'scanner-overlay';
                scannerOverlay.innerHTML = `
                    <div id="reader"></div>
                    <div class="scanner-ui-layer">
                        <div class="scan-frame"></div>
                        <p class="scan-instruction">Apunta al código QR</p>
                        <div class="scanner-controls">
                            <button id="close-scanner-btn">CERRAR</button>
                        </div>
                    </div>
                `;
                document.body.appendChild(scannerOverlay);

                html5QrCode = new Html5Qrcode("reader");

                // Configuración para forzar cámara trasera y alta calidad
                const config = { 
                    fps: 30, // 30 FPS para fluidez
                    qrbox: { width: 280, height: 280 },
                    aspectRatio: 1.777778, // Fullscreen
                    videoConstraints: {
                        width: { min: 720, ideal: 1280, max: 1920 }, // HD/FullHD
                        height: { min: 480, ideal: 720, max: 1080 },
                        facingMode: "environment", // 🔥 OBLIGATORIO: CÁMARA TRASERA
                        focusMode: "continuous"
                    }
                };

                // Iniciar escaneo usando la configuración de "environment" directamente
                await html5QrCode.start(
                    { facingMode: "environment" }, 
                    config,
                    (decodedText) => {
                        // ÉXITO
                        html5QrCode.stop().then(() => {
                            if(document.body.contains(scannerOverlay)) document.body.removeChild(scannerOverlay);
                            
                            ticketCodeInput.value = decodedText;
                            Notification(notificationContainer, 'Código detectado. Verificando...', 'info');
                            verifyAndProcessTicket();
                        });
                    },
                    (errorMessage) => { /* Ignorar errores de frame */ }
                );

                // Evento Cerrar
                document.getElementById('close-scanner-btn').addEventListener('click', async () => {
                    if (html5QrCode) {
                        try { await html5QrCode.stop(); } catch(e){}
                    }
                    if(document.body.contains(scannerOverlay)) document.body.removeChild(scannerOverlay);
                });

            } catch (error) {
                console.error(error);
                Notification(notificationContainer, 'Error de cámara. Verifique permisos.', 'error');
                const overlay = document.getElementById('scanner-overlay');
                if (overlay) document.body.removeChild(overlay);
            }
        };

        cameraScanBtn.addEventListener('click', startScanning);
    }

    setTimeout(() => ticketCodeInput.focus(), 100);

    return page;
}












